// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
// Results step for the company data upload workflow.
// Displays created, updated, not-updated, and failed companies with expandable sections.
import { Link } from '@/components/link';
import {
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  CheckRounded,
  ContentCopyRounded,
  Error as ErrorIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
} from '@/constants/hardfin-standard-icons';
import { CompanyDataUploadResult } from '@/customer/api/upload-company-data';
import type { ExpandedCompaniesState } from '@/customer/components/company-data-upload/types';
import { SparseCustomer } from '@/sparse/api/types';
import { formatNumber } from '@/utilities/number';
import { pluralize } from '@/utilities/pluralize';
import {
  createIdentifierSuffix,
  formatFailedRowsForCopy,
} from '@/utilities/upload-helpers';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  IconButton,
  ListItem,
  ListItemText,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { FixedSizeList as VirtualList } from 'react-window';

const ITEM_HEIGHT = 40;
const FAILED_ROW_HEIGHT = 80;

interface Props {
  uploadResult: CompanyDataUploadResult | null;
  expandedCompanies: ExpandedCompaniesState;
  onToggleExpanded: (section: keyof ExpandedCompaniesState) => void;
  onBack: () => void;
  onStartNew: () => void;
  onClose: () => void;
}

interface RowFailure {
  rowNumber: number;
  companyName: string;
  errors: string[];
}

interface CompanyRowProps {
  index: number;
  style: React.CSSProperties;
  data: SparseCustomer[];
}

const CompanyRow = (props: CompanyRowProps) => {
  const company = props.data[props.index];
  return (
    <div style={props.style}>
      <ListItem component="div" dense>
        <ListItemText
          primary={
            <Link
              to={`/companies/$companyKey`}
              params={{ companyKey: company.key }}
            >
              {company.name}
            </Link>
          }
        />
      </ListItem>
    </div>
  );
};

interface FailedRowItemProps {
  index: number;
  style: React.CSSProperties;
  data: RowFailure[];
}

const FailedRowItem = (props: FailedRowItemProps) => {
  const row = props.data[props.index];
  return (
    <div style={props.style}>
      <ListItem component="div" dense>
        <ListItemText
          primary={`For file row ${row.rowNumber}${row.companyName ? ` (company "${row.companyName}")` : ''}:`}
          secondary={
            <ul style={{ margin: 0, paddingLeft: '16px' }}>
              {row.errors.map((error, idx) => (
                <li key={idx}>{error}</li>
              ))}
            </ul>
          }
          secondaryTypographyProps={{ component: 'div' }}
        />
      </ListItem>
    </div>
  );
};

/**
 * Displays the results of a company data upload, including created, updated,
 * not-updated, and failed companies in expandable sections.
 *
 * @param props.uploadResult - The result of the upload operation
 * @param props.expandedCompanies - State tracking which result sections are expanded
 * @param props.onToggleExpanded - Callback to toggle a result section
 * @param props.onBack - Callback to go back to the upload step
 * @param props.onStartNew - Callback to start a new upload
 * @param props.onClose - Callback to close the dialog
 */
export function Results(props: Props) {
  const [isCopied, setIsCopied] = useState(false);

  if (!props.uploadResult) {
    return (
      <Card>
        <CardContent>
          <Typography color="error">
            No upload results available. Please try again.
          </Typography>
          <Button variant="contained" onClick={props.onStartNew} sx={{ mt: 2 }}>
            Start new upload
          </Button>
        </CardContent>
      </Card>
    );
  }

  const { success, items, structureErrors, rowErrors } = props.uploadResult;

  const companiesCreated: SparseCustomer[] = [];
  const companiesUpdated: SparseCustomer[] = [];
  const companiesNotUpdated: SparseCustomer[] = [];
  const failedRows: RowFailure[] = [];

  /**
   * Handles copying all failed row errors to clipboard with visual feedback
   */
  const handleCopyErrors = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const text = formatFailedRowsForCopy(failedRows, (row) =>
        createIdentifierSuffix(row, 'companyName', 'company')
      );
      await navigator.clipboard.writeText(text);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy errors:', error);
    }
  };

  rowErrors.forEach((row) => {
    if (row.errors.length > 0) {
      failedRows.push({
        rowNumber: row.index,
        companyName: '',
        errors: row.errors.map(
          (err) => `Column "${err.fieldName}" has an error: ${err.error}`
        ),
      });
      return;
    }
  });

  items.forEach((item) => {
    if (item.errors.length > 0) {
      failedRows.push({
        rowNumber: item.index,
        companyName: item.companyName,
        errors: item.errors.map(
          (err) => `Column "${err.fieldName}" has an error: ${err.error}`
        ),
      });
      return;
    }
    if (item.noOp && item.validatedCompany) {
      companiesNotUpdated.push(item.validatedCompany);
      return;
    }
    if (item.created && item.validatedCompany) {
      companiesCreated.push(item.validatedCompany);
      return;
    }
    if (item.updated && item.validatedCompany) {
      companiesUpdated.push(item.validatedCompany);
    }
  });

  const totalProcessed =
    companiesCreated.length +
    companiesUpdated.length +
    companiesNotUpdated.length;

  return (
    <Card>
      <CardContent>
        {success ? (
          <>
            <Alert severity="success" sx={{ mb: 3 }}>
              <AlertTitle>Success</AlertTitle>
              <Typography variant="body2">
                Your company data uploaded successfully for{' '}
                {pluralize(totalProcessed, {
                  singular: 'company',
                  plural: 'companies',
                })}
              </Typography>
            </Alert>

            {companiesCreated.length > 0 && (
              <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
                <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: 'success.light',
                    color: 'success.contrastText',
                    cursor: companiesCreated.length ? 'pointer' : undefined,
                  }}
                  onClick={
                    companiesCreated.length
                      ? () => props.onToggleExpanded('created')
                      : undefined
                  }
                >
                  <Box display="flex" alignItems="center">
                    <AddIcon sx={{ mr: 1 }} />
                    <Typography variant="subtitle1">
                      Companies created ({formatNumber(companiesCreated.length)}
                      )
                    </Typography>
                  </Box>
                  {companiesCreated.length > 0 &&
                    (props.expandedCompanies.created ? (
                      <ExpandLessIcon />
                    ) : (
                      <ExpandMoreIcon />
                    ))}
                </Box>
                <Collapse in={props.expandedCompanies.created}>
                  <VirtualList
                    height={Math.min(
                      companiesCreated.length * ITEM_HEIGHT,
                      200
                    )}
                    itemCount={companiesCreated.length}
                    itemSize={ITEM_HEIGHT}
                    itemData={companiesCreated}
                    width="100%"
                  >
                    {CompanyRow}
                  </VirtualList>
                </Collapse>
              </Paper>
            )}

            {companiesUpdated.length > 0 && (
              <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
                <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: 'success.light',
                    color: 'success.contrastText',
                    cursor: companiesUpdated.length ? 'pointer' : undefined,
                  }}
                  onClick={
                    companiesUpdated.length
                      ? () => props.onToggleExpanded('updated')
                      : undefined
                  }
                >
                  <Box display="flex" alignItems="center">
                    <CheckCircleIcon sx={{ mr: 1 }} />
                    <Typography variant="subtitle1">
                      Companies updated ({formatNumber(companiesUpdated.length)}
                      )
                    </Typography>
                  </Box>
                  {companiesUpdated.length > 0 &&
                    (props.expandedCompanies.updated ? (
                      <ExpandLessIcon />
                    ) : (
                      <ExpandMoreIcon />
                    ))}
                </Box>
                <Collapse in={props.expandedCompanies.updated}>
                  <VirtualList
                    height={Math.min(
                      companiesUpdated.length * ITEM_HEIGHT,
                      200
                    )}
                    itemCount={companiesUpdated.length}
                    itemSize={ITEM_HEIGHT}
                    itemData={companiesUpdated}
                    width="100%"
                  >
                    {CompanyRow}
                  </VirtualList>
                </Collapse>
              </Paper>
            )}

            {companiesNotUpdated.length > 0 && (
              <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
                <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: (theme) => theme.palette.grey.A400,
                    color: 'info.contrastText',
                    cursor: companiesNotUpdated.length ? 'pointer' : undefined,
                  }}
                  onClick={
                    companiesNotUpdated.length
                      ? () => props.onToggleExpanded('notUpdated')
                      : undefined
                  }
                >
                  <Box display="flex" alignItems="center">
                    <InfoIcon sx={{ mr: 1 }} />
                    <Typography variant="subtitle1">
                      Companies with no updates (
                      {formatNumber(companiesNotUpdated.length)})
                    </Typography>
                  </Box>
                  {companiesNotUpdated.length > 0 &&
                    (props.expandedCompanies.notUpdated ? (
                      <ExpandLessIcon />
                    ) : (
                      <ExpandMoreIcon />
                    ))}
                </Box>
                <Collapse in={props.expandedCompanies.notUpdated}>
                  <VirtualList
                    height={Math.min(
                      companiesNotUpdated.length * ITEM_HEIGHT,
                      200
                    )}
                    itemCount={companiesNotUpdated.length}
                    itemSize={ITEM_HEIGHT}
                    itemData={companiesNotUpdated}
                    width="100%"
                  >
                    {CompanyRow}
                  </VirtualList>
                </Collapse>
              </Paper>
            )}
          </>
        ) : (
          <>
            <Alert severity="error" sx={{ mb: 3 }}>
              {structureErrors.length > 0 && (
                <>
                  <AlertTitle>File format invalid</AlertTitle>
                  <Typography variant="body2">
                    The uploaded file has column names that do not match the
                    required company data fields
                  </Typography>
                </>
              )}
              {failedRows.length > 0 && (
                <>
                  <AlertTitle>Uploaded data invalid</AlertTitle>
                  <Typography variant="body2">
                    The uploaded file includes data that does not match your
                    companies or field requirements
                  </Typography>
                </>
              )}
            </Alert>
            {structureErrors && structureErrors.length > 0 && (
              <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
                <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: 'error.light',
                    color: 'error.contrastText',
                    cursor: 'pointer',
                  }}
                  onClick={() => props.onToggleExpanded('structureErrors')}
                >
                  <Box display="flex" alignItems="center">
                    <ErrorIcon sx={{ mr: 1 }} />
                    <Typography variant="subtitle1">
                      Mismatched columns ({formatNumber(structureErrors.length)}
                      )
                    </Typography>
                  </Box>
                  {props.expandedCompanies.structureErrors ? (
                    <ExpandLessIcon />
                  ) : (
                    <ExpandMoreIcon />
                  )}
                </Box>
                <Collapse in={props.expandedCompanies.structureErrors}>
                  <ul
                    style={{
                      maxHeight: '300px',
                      overflow: 'auto',
                      listStyleType: 'disc',
                    }}
                  >
                    {structureErrors.map((row) => (
                      <li
                        key={`structure-${row.fieldName}`}
                        style={{ paddingBottom: '8px' }}
                      >
                        Column "{`${row.fieldName}`}" not found
                      </li>
                    ))}
                  </ul>
                </Collapse>
              </Paper>
            )}
            {failedRows && failedRows.length > 0 && (
              <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
                <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: 'error.light',
                    color: 'error.contrastText',
                    cursor: 'pointer',
                  }}
                  onClick={() => props.onToggleExpanded('failed')}
                >
                  <Box display="flex" alignItems="center">
                    <ErrorIcon sx={{ mr: 1 }} />
                    <Typography variant="subtitle1">
                      Invalid rows ({formatNumber(failedRows.length)}{' '}
                      {pluralize(failedRows.length, {
                        includeCount: false,
                        singular: 'company',
                        plural: 'companies',
                      })}
                      )
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Tooltip
                      title={isCopied ? 'Copied!' : 'Copy all errors'}
                      arrow
                    >
                      <IconButton
                        size="small"
                        onClick={handleCopyErrors}
                        aria-label={isCopied ? 'Copied' : 'Copy all errors'}
                        sx={{
                          color: 'error.contrastText',
                          '&:hover': {
                            bgcolor: 'error.dark',
                          },
                        }}
                      >
                        {isCopied ? (
                          <CheckRounded fontSize="small" />
                        ) : (
                          <ContentCopyRounded fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                    {props.expandedCompanies.failed ? (
                      <ExpandLessIcon />
                    ) : (
                      <ExpandMoreIcon />
                    )}
                  </Box>
                </Box>
                <Collapse in={props.expandedCompanies.failed}>
                  <VirtualList
                    height={Math.min(
                      failedRows.length * FAILED_ROW_HEIGHT,
                      300
                    )}
                    itemCount={failedRows.length}
                    itemSize={FAILED_ROW_HEIGHT}
                    itemData={failedRows}
                    width="100%"
                  >
                    {FailedRowItem}
                  </VirtualList>
                </Collapse>
              </Paper>
            )}
          </>
        )}

        <Box display="flex" justifyContent="space-between" mt={3}>
          <Button variant="outlined" onClick={props.onBack}>
            Back
          </Button>
          <Box>
            <Button
              variant="outlined"
              onClick={props.onStartNew}
              sx={{ mr: 1 }}
            >
              Start new upload
            </Button>
            <Button variant="contained" onClick={props.onClose}>
              Close
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
